import { useTypeSafeMutation } from '@oresundsbron/api';
import {
  Banner,
  Box,
  Card,
  Grid,
  Input,
  LoadingButton,
  ReadOnlyField,
  Typography,
} from '@oresundsbron/bridge-ui';
import { useForm } from '@oresundsbron/use-form';
import { changePasswordParser, newPassword } from '@oresundsbron/validators';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import { PasswordRequirementNotice } from '../../components/PasswordRequirementNotice';
import { validate } from '../../lib/zod';
import { setIsPasswordSet, useSignUp } from './stores/useSignUp';
import { Selector } from '~/components/Forms/ContactInformation/Selector';
import { BankIDIcon } from '../account/Authentication/BankID/Icon';
import { UsernamePasswordIcon } from './UsernamePasswordIcon';
import { useRouter } from 'next/router';
import { resetAuthentication } from '~/stores/auth';

export type Data = {
  password: string;
  confirmPassword: string;
};

export const SetPassword: FC<{ ns: string }> = ({ ns }) => {
  const { t } = useTranslation([ns, 'common'], { nsMode: 'fallback' });
  const { resp, token } = useSignUp();
  const [loading, toggleLoading] = useState(false);
  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState(1);
  const { mutateAsync: setPassword, isError } = useTypeSafeMutation(
    ['password', 'set'],
    (req) => req.Customer.setPassword,
    {
      onMutate: () => toggleLoading(true),
      onSettled: (_, error) => {
        toggleLoading(false);

        if (!error) {
          setIsPasswordSet();
        }
      },
    }
  );
  const router = useRouter();
  const useAuth0 = process.env.NEXT_PUBLIC_USE_AUTH0 === 'true';

  const { mutateAsync: setPasswordV2, isError: isErrorV2 } =
    useTypeSafeMutation(
      ['password', 'set', 'V2'],
      (req) => req.Customer.setPasswordV2(token),
      {
        onMutate: () => toggleLoading(true),
        onSettled: (_, error) => {
          const isBankID = selectedAuthenticationMethod === 1;
          if (!isBankID) {
            toggleLoading(false);

            if (!error) {
              setIsPasswordSet();
            }
          }
        },
      }
    );

  const generateRandomString = (length: number = 20) => {
    const getRandomChar = () => {
      const charSets = [
        { start: 48, end: 57 }, // Numbers (0-9)
        { start: 65, end: 90 }, // Uppercase letters (A-Z)
        { start: 97, end: 122 }, // Lowercase letters (a-z)
        { start: 33, end: 47 }, // Symbols (! to /)
        { start: 58, end: 64 }, // Symbols (: to @)
        { start: 91, end: 96 }, // Symbols ([ to `)
        { start: 123, end: 126 }, // Symbols ({ to ~)
      ];
      // Randomly select a character set
      const charSet = charSets[Math.floor(Math.random() * charSets.length)];
      // Generate a random character within the selected set
      const charCode =
        Math.floor(Math.random() * (charSet.end - charSet.start + 1)) +
        charSet.start;
      return String.fromCharCode(charCode);
    };

    let result = '';
    for (let i = 0; i < length; i++) {
      result += getRandomChar();
    }
    const isValid = newPassword.safeParse({
      password: result,
      confirmPassword: result,
    });
    if (!isValid.success) {
      result = generateRandomString();
    }
    return result;
  };

  const {
    registerField,
    isValid,
    handleSubmit,
    validate: validateFn,
    getError,
  } = useForm<Data>({
    namespace: 'setPassword',
    onValidate: validate(changePasswordParser.spa, t),
    onSubmit: async (data) => {
      const useAuth0 = process.env.NEXT_PUBLIC_USE_AUTH0 === 'true';
      if (useAuth0) {
        const isBankID = selectedAuthenticationMethod === 1;
        const randomPassword = generateRandomString();
        const response = await setPasswordV2({
          password: isBankID ? randomPassword : data.password,
          confirmPassword: isBankID ? randomPassword : data.confirmPassword,
        });
        resetAuthentication();
        if (isBankID) {
          // BankID selected
          const locale = router.locale;
          await router.replace(
            `/api/auth/login?locale=${locale}&source=sebankid&nonce=${response.nonce}&returnUrl=${router.asPath}`
          );
        }
      } else {
        await setPassword({
          password: data.password,
          code: resp?.code,
          confirmPassword: data.confirmPassword,
        });
      }
    },
  });

  return (
    <>
      <Typography as="h1" intent="headline" className="col-span-full">
        {t('form.sections.password.title')}
      </Typography>
      <Typography className="col-span-full">
        {t('form.sections.password.description')}
      </Typography>

      <Card.Root as="form" onSubmit={handleSubmit} className="col-span-8">
        {useAuth0 ? (
          <Card.Content as={Grid}>
            <Selector
              items={[
                {
                  text: t('authenticationMethods.bankId'),
                  icon: <BankIDIcon height="2.5em" width="2.5em"></BankIDIcon>,
                  id: 1,
                },
                {
                  text: t('authenticationMethods.usernameAndPassword'),
                  icon: (
                    <UsernamePasswordIcon height="1.625em" width="1.625em" />
                  ),
                  id: 2,
                },
              ]}
              onChange={(e: number) => setSelectedAuthenticationMethod(e)}
            ></Selector>
          </Card.Content>
        ) : null}
        <Card.Content as={Grid}>
          {selectedAuthenticationMethod === 1 && useAuth0 ? (
            <Box className="col-span-full flex">
              <LoadingButton loadingLabel={t('action.loading')} type="submit">
                {t('action.next')}
              </LoadingButton>
            </Box>
          ) : (
            <>
              {(isError || isErrorV2) && (
                <Banner className="col-span-full" color="negative">
                  {t('errors.savePassword')}
                </Banner>
              )}
              <ReadOnlyField
                label={t('form.labels.customerNo')}
                className="col-span-full"
              >
                {resp?.customerNo}
              </ReadOnlyField>
              <Input
                className="col-span-full sm:col-span-4 md:col-span-6"
                {...registerField('password')}
                label={t('form.labels.password')}
                error={getError('password')}
                disabled={loading}
                type="password"
                required
              />
              <Input
                className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
                {...registerField('confirmPassword')}
                label={t('form.labels.repeatPassword')}
                error={getError('confirmPassword')}
                disabled={loading}
                type="password"
                required
              />
              <PasswordRequirementNotice
                listMode
                className="col-span-full"
                title={t('passwordRequirement.title')}
                ns="common"
                transKey="passwordRequirement.description"
              />
              <Box className="col-span-full flex justify-end">
                <LoadingButton
                  loadingLabel={t('action.loading')}
                  type="submit"
                  disabled={!isValid || loading || isError || isErrorV2}
                  onClick={() => validateFn()()}
                  isLoading={loading}
                >
                  {t('action.savePassword')}
                </LoadingButton>
              </Box>
            </>
          )}
        </Card.Content>
      </Card.Root>
    </>
  );
};
